"use client";

import { useAuthenticated, useSettings } from "@gettactic/hooks";
import { useMapBuilderMappedResourcesData } from "@gettactic/map-builder/src/hooks/useMapBuilderMappedResourcesData";
import {
  MapBuilderViewer,
  type NewMapViewProps
} from "@gettactic/map-builder/src/MapBuilderViewer";
import { MapSvgView } from "@gettactic/map-editor";
import React from "react";
import { useSvgMapEditorEnabled } from "@/lib/hooks/useSvgMapEditorEnabled";

/**
 * The floor's viewer: the current one, or the SVG editor's renderer for a
 * person on the map builder v2 flag (`localStorage.mapBuilder = "2"`, the
 * same switch as the editor). Same props and the same `ViewSVG`-shaped
 * element either way, so the page's own pan, zoom and centring keep
 * working.
 */
export const MapViewerSwitch = React.forwardRef<
  SVGSVGElement | null,
  NewMapViewProps
>((props, ref) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const { isOfficePrivacyEnabled } = useSettings();
  const floorData = useMapBuilderMappedResourcesData(
    props.floor,
    props.resources
  );
  const isSvgEnabled = useSvgMapEditorEnabled();
  const useSvgRenderer = isSvgEnabled && !!props.floor;

  if (!useSvgRenderer || !props.floor) {
    return <MapBuilderViewer {...props} ref={ref} />;
  }

  return (
    <MapSvgView
      key={props.floor.id}
      ref={ref}
      value={floorData}
      initialPosition={props.mapEditPosition || undefined}
      interactiveZones={props.interactiveZoneInfo}
      highlightResource={props.highlightResource}
      onZoneLinkClick={props.onZoneLinkClick}
      authenticatedUser={authenticatedUser}
      isPrivacyMode={isOfficePrivacyEnabled(currentOffice?.id)}
      resourceColors={props.resourceColors}
      zoneColors={props.zoneColors}
      isDark={props.isDark}
      style={props.style}
      focusBounds={props.focusBounds}
      hideZoneLegend={props.hideZoneLegend}
      legendBottomOffset={props.legendBottomOffset}
    >
      {props.children}
    </MapSvgView>
  );
});
MapViewerSwitch.displayName = "MapViewerSwitch";
